import { hammerEffect, HAMMER_ID } from './effects/hammer';
import { colorDeleteEffect, COLOR_DELETE_ID } from './effects/color-delete';
import { rainbowGatesEffect, RAINBOW_GATES_ID } from './effects/rainbow-gates';
import { surgeEffect, SURGE_ID } from './effects/surge';
import { gateWidenerEffect, GATE_WIDENER_ID } from './effects/gate-widener';
/** Registered effects by power-up id. */
export const POWERUP_EFFECTS = {
    [HAMMER_ID]: hammerEffect,
    [COLOR_DELETE_ID]: colorDeleteEffect,
    [RAINBOW_GATES_ID]: rainbowGatesEffect,
    [SURGE_ID]: surgeEffect,
    [GATE_WIDENER_ID]: gateWidenerEffect,
};
/** Module 90 — see {@link PowerupEffect.chargesWhenSpent}. */
export function chargesWhenSpent(id) {
    return POWERUP_EFFECTS[id]?.chargesWhenSpent === true;
}
/**
 * Module 90 — the power-ups whose mode is open on this board and not yet paid
 * for, in registry order. Derived from the board rather than stored beside it,
 * so "open" and "unpaid" can never disagree: the charge and the close are the
 * same moment, and a level change or reload drops both together.
 *
 * Module 91 — a list, not one id: Master Key became pay-when-used too, and it
 * can be open at the same time as Wild Gates (user decision D5). Every caller
 * that held "the" open one — the coin hold, the cancel, the button's "on"
 * state — now has to account for each.
 */
export function openUnpaidPowerups(state) {
    return Object.entries(POWERUP_EFFECTS)
        .filter(([, effect]) => effect.chargesWhenSpent && effect.isOpen?.(state))
        .map(([id]) => id);
}
/** Whether an id has a real effect yet — see the header. */
export function hasEffect(id) {
    return id in POWERUP_EFFECTS;
}
/**
 * Valid targets for a power-up. With no effect registered every block on the
 * board is a target: a power-up added to config without an effect is aimed at a
 * block, so "any block" is the correct default and each effect narrows it only
 * if it needs to.
 */
export function targetsFor(id, state) {
    const effect = POWERUP_EFFECTS[id];
    return effect ? effect.targets(state) : state.blocks.map((b) => b.id);
}
/**
 * How this power-up commits (WP-50.27). `'target'` for anything that has not
 * said otherwise, including an id with no effect registered — the old flow stays
 * the default, so adding the second shape changed no existing power-up.
 */
export function activationFor(id) {
    return POWERUP_EFFECTS[id]?.activation ?? 'target';
}
/**
 * Switch the board into an `'immediate'` power-up's mode, or `null` when there is
 * nothing to do — the id declares no such effect, or the mode is already open, or
 * the board could not use it.
 *
 * **The plugin calls this BEFORE it charges**, and that order is the whole
 * protection: a `null` after a successful charge would be the player paying for
 * nothing. Asking first makes a double press free rather than a second purchase.
 *
 * Every `'immediate'` effect must therefore implement `activate`, which is pinned
 * by a test in `effects/rainbow-gates.test.ts` rather than by the type system —
 * an optional method on a shared interface cannot express "required when this
 * other field is set".
 */
export function activateEffect(id, state) {
    return POWERUP_EFFECTS[id]?.activate?.(state) ?? null;
}
/**
 * Module 70 — is there anything for this power-up to do on this board?
 *
 * Read by `PowerupController._statusOf`, so a board-dead power-up dims and says
 * why instead of sitting bright and swallowing the tap. Defaults to `true` for
 * every effect that declares nothing, and for an unregistered id — a power-up
 * with no effect is refused elsewhere and must not gain a second reason here.
 */
export function effectAvailableOn(id, state) {
    return POWERUP_EFFECTS[id]?.availableOn?.(state) ?? true;
}
/**
 * Module 67 (WP-67.B4) — the gate keys a `'target-gate'` power-up may be aimed
 * at. Empty for every other shape (and for an unregistered id): unlike block
 * targeting there is no "every gate" default, because an effect that changes a
 * gate without declaring which gates it can change is a bug, not a fallback.
 */
export function gateTargetsFor(id, state) {
    return POWERUP_EFFECTS[id]?.gateTargets?.(state) ?? [];
}
/** Apply a `'target-gate'` power-up to one gate, or `null` when nothing to do. */
export function applyGateEffect(id, state, gateKey) {
    return POWERUP_EFFECTS[id]?.applyToGate?.(state, gateKey) ?? null;
}
/** Apply a power-up to a target, or `null` when nothing is registered yet. */
export function applyEffect(id, state, targetBlockId) {
    return POWERUP_EFFECTS[id]?.apply(state, targetBlockId) ?? null;
}
